import React from 'react';
import LegalPage, { COMPANY_NAME, List, P, SUPPORT_EMAIL } from './LegalPage';

const EFFECTIVE = 'October 4, 2026';

/** The Privacy Policy: exactly what JobFlo collects, why, who sees it, how long it stays, and what the person can do. Written from what the code does. */
const PrivacyPage: React.FC = () => (
  <LegalPage
    title="Privacy Policy"
    effective={EFFECTIVE}
    intro={
      <P>
        This policy explains what information {COMPANY_NAME} ("we", "us") collects when you use the JobFlo website, the
        JobFlo browser extension and the services behind them, what we do with it, and the choices you have. We wrote it to
        match what JobFlo actually does; if something here is unclear, ask us at {SUPPORT_EMAIL}.
      </P>
    }
    sections={[
      {
        title: 'What we collect',
        body: (
          <>
            <P>
              <strong>Account.</strong> When you sign in with Google we receive your name, email address and Google account
              id. If you created an account with a password, we store your email, name and a hashed password.
            </P>
            <P>
              <strong>What you give JobFlo to fill applications.</strong> Your profile (contact details, address, work
              experience, education, links), the answers you save to application questions, and the resumes you upload. Answers
              to questions about work eligibility, demographics (such as gender, race, veteran or disability status) and other
              sensitive matters are stored only if you enter them yourself; JobFlo never infers them.
            </P>
            <P>
              <strong>Applications.</strong> The jobs you save and apply to, when, with which resume, the answers that were
              filled, and the status of each application.
            </P>
            <P>
              <strong>Tailored resumes.</strong> The text of your resume as read into its parts, the job description, the
              tailored version, what was changed and why, and the resulting PDF.
            </P>
            <P>
              <strong>Your Experience library, only if you add to it.</strong> What you write about your work, and the text
              of the documents you add (the file itself is not kept). It is used to tailor your resumes and to show how you
              match a job. Do not add material your employer or a client holds confidential.
            </P>
            <P>
              <strong>Email tracking, only if you turn it on.</strong> Emails sent to your JobFlo address, or copies your
              mailbox forwards to it: sender, subject, date and body, used to match the email to an application and update its
              status. We keep the full message for 30 days and the summary with the application.
            </P>
            <P>
              <strong>Your own API keys, only if you add them.</strong> If you choose to run tailored resumes on your own
              account with a model vendor, we store that key encrypted and never show it again.
            </P>
            <P>
              <strong>Technical information.</strong> Server logs with IP address, browser type, the pages or API calls
              requested, and errors, kept for a month. The dashboard keeps small settings (such as your filters or the
              "prefill on open" choice) in your browser’s local storage.
            </P>
          </>
        ),
      },
      {
        title: 'What the browser extension does',
        body: (
          <>
            <P>
              The extension runs on the application pages of the job sites it supports. There it reads the form’s fields
              to work out what each asks, fills them from your JobFlo data when you ask, and records the application when you
              submit it, together with the answers that were filled and those you typed yourself. What you type for a
              question about work eligibility, pay or demographics is not recorded unless you choose to save it. It reads the
              job title, company and description from the page to track the application and to build a tailored resume. It
              does not read other pages, your browsing history, or anything you type on other sites. It never submits a form. The extension talks only to app.jobflo.me and to the job sites themselves.
            </P>
            <P>
              The extension’s use of the information it handles follows the Chrome Web Store User Data Policy, including its
              Limited Use requirements. That information is used only to fill and track your applications and to build your
              tailored resumes. It is not sold, not used for advertising, not used to decide credit or lending, and not read
              by people at JobFlo except with your agreement, to keep the service secure, or when the law requires it.
            </P>
          </>
        ),
      },
      {
        title: 'How we use information',
        body: (
          <>
            <List
              items={[
                'to run JobFlo: fill applications, build tailored resumes, track applications, forward and read the emails you chose to route through JobFlo;',
                'to keep your account secure and JobFlo working: sign-in, abuse prevention, error diagnosis;',
                'to bill you, if you are on a paid plan;',
                'to improve JobFlo. Tailored resumes whose application went on to an interview or an offer are kept, with that outcome, so we can learn what such resumes have in common and help people write better ones. We look at them in aggregate; we do not share them.',
                'to email you what you asked for, such as the job alerts of a saved search (each has a link to stop), and to contact you about your account, such as a change to these terms. We do not send marketing email.',
              ]}
            />
          </>
        ),
      },
      {
        title: 'Who else sees it',
        body: (
          <>
            <P>We do not sell your information. It is shared only with:</P>
            <List
              items={[
                <>
                  <strong>Employers</strong>, when you apply: whatever you put in their form, including your resume.
                </>,
                <>
                  <strong>Language model vendors</strong>, when a tailored resume is built: the text of your resume, of what
                  you added to your Experience library, and the job description are sent to the vendor (OpenAI, unless you chose another vendor with your own key) to be
                  processed. We use API terms under which the vendor does not use the data to train its models.
                </>,
                <>
                  <strong>Service providers that host JobFlo:</strong> Amazon Web Services (servers, file storage and
                  receiving email, in the United States), Resend (sending email: employer messages passed on to you and
                  job alerts), Neon (database), Google (sign-in), and, for paid plans, a
                  payment provider that handles your card details so we never see them.
                </>,
                'Authorities, when the law requires it.',
              ]}
            />
          </>
        ),
      },
      {
        title: 'How long we keep it',
        body: (
          <>
            <List
              items={[
                'Your account, profile, answers, resumes and applications: until you delete them or your account.',
                'Tailored resumes: 30 days after they are built, then deleted with their file. A tailored resume whose application went further than a submission (an interview or an offer) is kept. One whose application is still waiting for an answer is kept up to 90 days.',
                'Emails handled by email tracking: the full message for 30 days; the sender, subject and what it meant for the application stay with the application.',
                'Server logs: one month.',
                'Payment records: as long as tax law requires.',
              ]}
            />
          </>
        ),
      },
      {
        title: 'Your choices and rights',
        body: (
          <>
            <List
              items={[
                'You can see and change your profile, answers and resumes at any time in JobFlo.',
                'You can turn email tracking off at any time.',
                'You can delete your account from the Profile page. That removes your profile, answers, resumes, Experience library, tailored resumes and their files, applications, email tracking and saved searches at once. Cost records keep no link to you.',
                'You can ask us for a copy of your information, or to correct or delete it, at the address below. Depending on where you live you may have further rights (for example under the GDPR or the CCPA), including to complain to a supervisory authority; we honour them.',
              ]}
            />
          </>
        ),
      },
      {
        title: 'Security',
        body: (
          <>
            <P>
              Data travels encrypted (HTTPS) and is stored on encrypted disks. Keys you give us are encrypted with a key kept
              apart from the database. Access is limited to what running JobFlo needs. No system is perfectly secure; if we
              learn of a breach affecting you, we will tell you.
            </P>
          </>
        ),
      },
      {
        title: 'Children',
        body: <P>JobFlo is not for anyone under 16, and we do not knowingly collect information from them.</P>,
      },
      {
        title: 'Where the data lives',
        body: (
          <P>
            JobFlo runs in the United States. If you use it from elsewhere, your information is transferred to and processed in
            the United States.
          </P>
        ),
      },
      {
        title: 'Changes to this policy',
        body: (
          <P>
            We may update this policy. For a material change we will tell you by email or in JobFlo before it takes effect.
          </P>
        ),
      },
      {
        title: 'Contact',
        body: <P>Questions or requests about your information: {SUPPORT_EMAIL}.</P>,
      },
    ]}
  />
);

export default PrivacyPage;
